<form [formGroup]="form">
  <mat-dialog-content
    class="dialog-content"
    role="dialog"
    aria-modal="true"
    aria-labelledby="create-post-title"
  >
    <p id="create-post-title">Create Post</p>

    <quill-editor
      (onEditorCreated)="onEditorCreated($event)"
      class="editor"
      formControlName="content"
      [modules]="quillConfig"
      theme="snow"
      aria-label="Post content editor"
    >
    </quill-editor>

    <div class="row toggle-row">
      <button
        mat-stroked-button
        type="button"
        (click)="filterSwitch()"
        [attr.aria-expanded]="filter"
        aria-label="Toggle audience filters"
      >
        Audience Filters
      </button>

      @if (showCreateEventOption) {
        <mat-slide-toggle
          formControlName="createEvent"
          aria-label="Also create an event for this post"
          data-testid="post-create-event-toggle"
        >
          Also create an event
        </mat-slide-toggle>
      }
    </div>

    @if (events().length > 0) {
      <div class="filter-group filter-group--strict">
        <h4 class="filter-group__title">
          Linked Events
          <span class="filter-group__hint"
            >Shared events you've created, shown to this post's audience</span
          >
        </h4>
        <div class="filter-group__fields">
          <mat-form-field appearance="outline">
            <mat-label>Link existing events</mat-label>
            <mat-select
              formControlName="event_ids"
              multiple
              aria-label="Link existing events to this post"
              data-testid="post-link-events-select"
            >
              @for (e of events(); track e.id) {
                <mat-option [value]="e.id">{{ eventLabel(e) }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
      </div>
    }

    @if (filter === true) {
      <div class="filter-group filter-group--soft">
        <h4 class="filter-group__title">
          Recommendation Tags
          <span class="filter-group__hint">Boosts visibility — doesn't hide the post</span>
        </h4>
        <div class="filter-group__fields">
          <mat-form-field appearance="outline">
            <mat-label>Careers</mat-label>
            <mat-select
              formControlName="careers"
              multiple
              aria-label="Select careers to boost recommendation"
            >
              @for (c of careers; track c.id) {
                <mat-option [value]="c.id">{{ c.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Interests</mat-label>
            <mat-select
              formControlName="interests"
              multiple
              aria-label="Select interests to boost recommendation"
            >
              @for (c of interests; track c.id) {
                <mat-option [value]="c.id">{{ c.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
      </div>

      <div class="filter-group filter-group--strict">
        <h4 class="filter-group__title">
          Grade Restriction
          <span class="filter-group__hint">Only selected grades will see this post</span>
        </h4>
        <div class="filter-group__fields">
          <mat-form-field appearance="outline">
            <mat-label>Grades</mat-label>
            <mat-select
              formControlName="grades"
              multiple
              aria-label="Restrict post visibility to selected grades"
            >
              @for (c of grades; track c) {
                <mat-option [value]="c">{{ c }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>
      </div>

      @if (groups().length > 0) {
        <div class="filter-group filter-group--strict">
          <h4 class="filter-group__title">
            Group Restriction
            <span class="filter-group__hint">Only selected groups will see this post</span>
          </h4>
          <div class="filter-group__fields">
            <mat-form-field appearance="outline">
              <mat-label>Groups</mat-label>
              <mat-select
                formControlName="groups"
                multiple
                aria-label="Restrict post visibility to selected groups"
              >
                @for (g of groups(); track g.id) {
                  <mat-option [value]="g.id">{{ g.name }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
        </div>
      }
    }

    @if (showCreateEventOption && form.get('createEvent')?.value) {
      <div formGroupName="event" class="filter-group filter-group--strict">
        <h4 class="filter-group__title">
          Event Details
          <span class="filter-group__hint"
            >Uses the grade/group restrictions above as its audience</span
          >
        </h4>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Event title</mat-label>
          <input
            matInput
            formControlName="title"
            placeholder="Event title"
            aria-label="Event title"
            data-testid="post-event-title-input"
          />
        </mat-form-field>

        <div class="row">
          <mat-form-field appearance="outline" class="grow">
            <mat-label>Date</mat-label>
            <input
              matInput
              [matDatepicker]="postEventDatePicker"
              formControlName="day"
              aria-label="Event date"
              data-testid="post-event-date-input"
              readonly
              (click)="postEventDatePicker.open()"
            />
            <mat-datepicker-toggle
              matIconSuffix
              [for]="postEventDatePicker"
              aria-label="Open calendar"
            ></mat-datepicker-toggle>
            <mat-datepicker #postEventDatePicker></mat-datepicker>
          </mat-form-field>

          <mat-form-field appearance="outline" class="grow">
            <mat-label>Start time</mat-label>
            <input
              matInput
              formControlName="start_time"
              placeholder="10:00 AM"
              aria-label="Event start time"
              data-testid="post-event-start-time-input"
              [matAutocomplete]="postStartTimeAuto"
            />
            <mat-autocomplete #postStartTimeAuto="matAutocomplete">
              @for (t of filterTimeOptions(form.get('event.start_time')?.value); track t) {
                <mat-option [value]="t">{{ t }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline" class="grow">
            <mat-label>End time</mat-label>
            <input
              matInput
              formControlName="end_time"
              placeholder="11:00 AM"
              aria-label="Event end time (optional)"
              data-testid="post-event-end-time-input"
              [matAutocomplete]="postEndTimeAuto"
            />
            <mat-autocomplete #postEndTimeAuto="matAutocomplete">
              @for (t of filterTimeOptions(form.get('event.end_time')?.value); track t) {
                <mat-option [value]="t">{{ t }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        </div>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Location</mat-label>
          <input
            matInput
            formControlName="place"
            placeholder="Where is this happening?"
            aria-label="Event location"
            data-testid="post-event-location-input"
          />
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Event description</mat-label>
          <textarea
            matInput
            formControlName="description"
            rows="3"
            placeholder="Details for the event"
            aria-label="Event description"
            data-testid="post-event-description-input"
            class="no-resize"
          ></textarea>
        </mat-form-field>
      </div>
    }
  </mat-dialog-content>

  <mat-dialog-actions align="end">
    @if (showDraftOption) {
      <button
        mat-stroked-button
        type="button"
        (click)="save('draft')"
        aria-label="Save as draft"
        data-testid="post-save-draft-btn"
      >
        Save Draft
      </button>
    }

    <button
      mat-button
      type="button"
      (click)="save('edited')"
      aria-label="Publish post"
      data-testid="post-publish-btn"
    >
      Post
    </button>

    <button mat-button mat-dialog-close type="button" aria-label="Cancel post creation">
      Cancel
    </button>
  </mat-dialog-actions>
</form>
